/**
 * Secure Storage Utility for Admin Dashboard
 *
 * Security measures:
 * - Access tokens are kept in memory only (secure against XSS)
 * - Refresh tokens use localStorage by default (persistent across tabs/sessions)
 * - User data uses the same storage as refresh token
 *
 * For production, consider implementing httpOnly cookies via backend
 */

// In-memory storage for access token (most secure against XSS)
let inMemoryAccessToken = null;

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  REMEMBER_ME: 'remember_me',
  ACCESS_CONTROL: 'access_control',
};

/**
 * Check if storage is available
 * @param {Storage} storage - localStorage or sessionStorage
 * @returns {boolean}
 */
const checkStorageAvailable = (storage) => {
  try {
    storage.setItem('__test__', 'test');
    storage.removeItem('__test__');
    return true;
  } catch {
    return false;
  }
};

const isSessionStorageAvailable = checkStorageAvailable(sessionStorage);
const isLocalStorageAvailable = checkStorageAvailable(localStorage);

if (!isSessionStorageAvailable) {
  console.error('SessionStorage not available. Authentication may not work properly.');
}

/**
 * Check if "Remember Me" is enabled
 * @returns {boolean}
 */
export const isRememberMeEnabled = () => {
  if (!isLocalStorageAvailable) return false;
  return localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
};

/**
 * Get the appropriate storage - always use localStorage for persistence
 * This ensures sessions survive tab closes and browser restarts
 * @returns {Storage}
 */
const getStorage = () => {
  if (isLocalStorageAvailable) {
    return localStorage;
  }
  return sessionStorage;
};

/**
 * Check if any storage is available
 * @returns {boolean}
 */
const isAnyStorageAvailable = () => {
  return isSessionStorageAvailable || isLocalStorageAvailable;
};

/**
 * Set "Remember Me" preference
 * @param {boolean} enabled - Whether to remember the user
 */
export const setRememberMe = (enabled) => {
  if (!isLocalStorageAvailable) return;

  if (enabled) {
    localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
  } else {
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
  }
};

/**
 * Migrate existing data when remember me setting changes
 * Called after login to move tokens to the correct storage
 */
const migrateTokensToCurrentStorage = () => {
  const targetStorage = getStorage();
  const sourceStorage = targetStorage === localStorage ? sessionStorage : localStorage;

  try {
    // Move refresh token if exists in source but not in target
    const sourceToken = sourceStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (sourceToken && !targetStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)) {
      targetStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, sourceToken);
    }

    // Move user data if exists in source but not in target
    const sourceUser = sourceStorage.getItem(STORAGE_KEYS.USER);
    if (sourceUser && !targetStorage.getItem(STORAGE_KEYS.USER)) {
      targetStorage.setItem(STORAGE_KEYS.USER, sourceUser);
    }

    // Clear source storage (except remember_me which stays in localStorage)
    sourceStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    sourceStorage.removeItem(STORAGE_KEYS.USER);
  } catch (e) {
    console.error('Migration error:', e);
  }
};

/**
 * Initialize storage - check for existing tokens on page load
 * This handles the case where user refreshes the page
 */
const initializeStorage = () => {
  // Check localStorage first (primary storage)
  if (isLocalStorageAvailable) {
    const localToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (localToken) {
      // Token exists in localStorage, we're good
      return;
    }
  }

  // Clean up legacy keys from older code versions
  if (isLocalStorageAvailable) {
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('access_token');
  }

  // Check sessionStorage for legacy tokens and migrate them to localStorage
  if (isSessionStorageAvailable) {
    const sessionToken = sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (sessionToken && isLocalStorageAvailable) {
      // Migrate token from sessionStorage to localStorage
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, sessionToken);
      sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

      // Also migrate user data if present
      const sessionUser = sessionStorage.getItem(STORAGE_KEYS.USER);
      if (sessionUser) {
        localStorage.setItem(STORAGE_KEYS.USER, sessionUser);
        sessionStorage.removeItem(STORAGE_KEYS.USER);
      }
      return;
    }
  }
};

// Run initialization on module load
initializeStorage();

// Restore access token from localStorage into memory on load
if (isLocalStorageAvailable) {
  const storedAccessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (storedAccessToken) {
    inMemoryAccessToken = storedAccessToken;
  }
}

/**
 * Token Storage Functions
 */

/**
 * Set access token - stored in memory and localStorage
 * @param {string} token - JWT access token
 */
export const setAccessToken = (token) => {
  if (!token || typeof token !== 'string') {
    console.error('Invalid access token');
    return;
  }
  inMemoryAccessToken = token;
  const storage = getStorage();
  storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
};

/**
 * Get access token from memory, falling back to localStorage
 * @returns {string|null} - JWT access token or null
 */
export const getAccessToken = () => {
  if (inMemoryAccessToken) return inMemoryAccessToken;

  // Restore from persistent storage (e.g. after page refresh)
  const storage = getStorage();
  const storedToken = storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (storedToken) {
    inMemoryAccessToken = storedToken;
  }
  return inMemoryAccessToken;
};

/**
 * Set refresh token in appropriate storage based on remember me setting
 * @param {string} token - JWT refresh token
 * @param {boolean} rememberMe - Whether to persist across browser sessions
 */
export const setRefreshToken = (token, rememberMe = undefined) => {
  if (!isAnyStorageAvailable()) return;
  if (!token || typeof token !== 'string') {
    console.error('Invalid refresh token');
    return;
  }

  // If rememberMe is explicitly provided, update the setting
  if (rememberMe !== undefined) {
    setRememberMe(rememberMe);
  }

  // Migrate any existing tokens to the correct storage
  migrateTokensToCurrentStorage();

  // Store in the appropriate storage
  const storage = getStorage();
  storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
};

/**
 * Get refresh token from storage
 * Checks both localStorage and sessionStorage for flexibility
 * @returns {string|null} - JWT refresh token or null
 */
export const getRefreshToken = () => {
  // First check the preferred storage based on remember me
  const preferredStorage = getStorage();
  let token = preferredStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

  if (token) return token;

  // Fallback: check the other storage (handles edge cases)
  const fallbackStorage = preferredStorage === localStorage ? sessionStorage : localStorage;
  token = fallbackStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

  return token;
};

/**
 * Set user data in storage
 * Only store non-sensitive user info
 * @param {object} user - User object (id, email, name, etc.)
 */
export const setUser = (user) => {
  if (!isAnyStorageAvailable()) return;
  if (!user || typeof user !== 'object') {
    console.error('Invalid user data');
    return;
  }
  // Only store non-sensitive fields
  const safeUser = {
    id: user.id,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    is_verified: user.is_verified,
    role:user.employee.role,
  };
  const storage = getStorage();
  storage.setItem(STORAGE_KEYS.USER, JSON.stringify(safeUser));
};

/**
 * Get user data from storage
 * @returns {object|null} - User object or null
 */
export const getUser = () => {
  if (!isAnyStorageAvailable()) return null;

  try {
    // Check preferred storage first
    const storage = getStorage();
    let userStr = storage.getItem(STORAGE_KEYS.USER);

    // Fallback to other storage
    if (!userStr) {
      const fallbackStorage = storage === localStorage ? sessionStorage : localStorage;
      userStr = fallbackStorage.getItem(STORAGE_KEYS.USER);
    }

    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  } catch (e) {
    console.error('Error parsing user from storage:', e);
    return null;
  }
};

/**
 * Set access control data in storage
 * @param {Array|Object} accessControl - Array of module permissions or object with modules
 */
export const setAccessControl = (accessControl) => {
  if (!isAnyStorageAvailable()) return;
  if (!accessControl) {
    console.error('Invalid access control data: data is null or undefined');
    return;
  }

  // Handle both array and object responses
  let dataToStore = accessControl;

  // If it's an object with a 'modules' or 'data' property, extract it
  if (typeof accessControl === 'object' && !Array.isArray(accessControl)) {
    if (accessControl.modules && Array.isArray(accessControl.modules)) {
      dataToStore = accessControl.modules;
    } else if (accessControl.data && Array.isArray(accessControl.data)) {
      dataToStore = accessControl.data;
    } else if (accessControl.access_control && Array.isArray(accessControl.access_control)) {
      dataToStore = accessControl.access_control;
    }
  }

  // Final validation
  if (!Array.isArray(dataToStore)) {
    console.error('Invalid access control data: expected array but got', typeof dataToStore, dataToStore);
    return;
  }

  const storage = getStorage();
  storage.setItem(STORAGE_KEYS.ACCESS_CONTROL, JSON.stringify(dataToStore));
};

/**
 * Get access control data from storage
 * @returns {Array|null} - Access control array or null
 */
export const getAccessControl = () => {
  if (!isAnyStorageAvailable()) return null;

  try {
    const storage = getStorage();
    let acStr = storage.getItem(STORAGE_KEYS.ACCESS_CONTROL);

    if (!acStr) {
      const fallbackStorage = storage === localStorage ? sessionStorage : localStorage;
      acStr = fallbackStorage.getItem(STORAGE_KEYS.ACCESS_CONTROL);
    }

    if (acStr) {
      return JSON.parse(acStr);
    }
    return null;
  } catch (e) {
    console.error('Error parsing access control from storage:', e);
    return null;
  }
};

/**
 * Clear all auth-related data from both storages
 */
export const clearAuthData = () => {
  inMemoryAccessToken = null;

  // Clear from sessionStorage
  if (isSessionStorageAvailable) {
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.USER);
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_CONTROL);
  }

  // Clear from localStorage (except remember_me preference)
  if (isLocalStorageAvailable) {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    localStorage.removeItem(STORAGE_KEYS.ACCESS_CONTROL);
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean} - True if access token exists in memory
 */
export const isAuthenticated = () => {
  return !!getAccessToken();
};

/**
 * Check if user has a valid session (refresh token exists)
 * This is true even if access token is not in memory yet
 * @returns {boolean} - True if refresh token exists
 */
export const hasSession = () => {
  return !!getRefreshToken();
};

/**
 * Restore access token from refresh token
 * Called after page refresh to restore session
 * @param {function} refreshFn - Function that calls refresh API
 * @returns {Promise<boolean>} - True if session restored
 */
export const restoreSession = async (refreshFn) => {
  if (inMemoryAccessToken) return true;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const newAccessToken = await refreshFn(refreshToken);
    if (newAccessToken) {
      setAccessToken(newAccessToken);
      return true;
    }
  } catch (e) {
    console.error('Failed to restore session:', e);
    clearAuthData();
  }
  return false;
};

export default {
  setAccessToken,
  getAccessToken,
  setRefreshToken,
  getRefreshToken,
  setUser,
  getUser,
  setAccessControl,
  getAccessControl,
  clearAuthData,
  isAuthenticated,
  hasSession,
  restoreSession,
  setRememberMe,
  isRememberMeEnabled,
};
